/**
 * Direct checkout ("Pay now")
 * ---------------------------
 * Turns every add-to-cart submission into a one-click checkout:
 *
 *   1. Empties the cart (so the buyer only ever pays for the item they clicked).
 *   2. Adds the selected variant via the AJAX Cart API.
 *   3. Navigates straight to /checkout.
 *
 * The cart drawer and cart page are never opened.
 *
 * Implementation notes:
 * - This is a plain (non-module) deferred script with no imports, so it costs one
 *   small request and no module-graph resolution on the critical path.
 * - The `submit` listener is registered on `document` in the CAPTURE phase, which
 *   guarantees it runs before Horizon's `product-form-component` handler (bound on a
 *   descendant element) no matter which script hydrates first. `stopImmediatePropagation`
 *   then keeps the theme's own add-to-cart/cart-drawer pipeline from ever running.
 * - Every add-to-cart form also carries a `return_to=/checkout` hidden input, so a
 *   submit that happens before this script runs (or with JS disabled) still lands on
 *   checkout via a native form POST.
 */
(function () {
  'use strict';

  // `Theme` is declared with `const` in a classic script, so it lives in the global
  // lexical scope rather than on `window` — reference it by name, guarded.
  var THEME = typeof Theme !== 'undefined' && Theme ? Theme : {};
  var CONFIG = THEME.directCheckout || {};

  if (!CONFIG.enabled) return;

  var ROUTES = THEME.routes || {};
  var ADD_URL = ROUTES.cart_add_url || '/cart/add.js';
  var CLEAR_URL = CONFIG.clearUrl || '/cart/clear.js';
  var CHECKOUT_URL = CONFIG.checkoutUrl || '/checkout';
  var REPLACE_CART = CONFIG.replaceCart !== false;

  var STRINGS = {
    generic: CONFIG.errorGeneric || 'Something went wrong. Please try again.',
    unavailable: CONFIG.errorUnavailable || 'This item is unavailable.',
  };

  /** Guards against double submits / double charges while a redirect is pending. */
  var inFlight = false;

  /**
   * The button the buyer physically pressed. The sticky bar adds to cart by calling
   * `.click()` on the (offscreen) main button, so `event.submitter` is not always the
   * control that should show the pending state.
   * @type {HTMLElement | null}
   */
  var lastPressedButton = null;

  /* ------------------------------------------------------------------ *
   * Helpers
   * ------------------------------------------------------------------ */

  /**
   * @param {EventTarget | null} form
   * @returns {form is HTMLFormElement}
   */
  /**
   * Every control that can start an add to cart, including the ones in custom sections
   * that post their own form to /cart/add.
   */
  var BUTTON_SELECTOR = [
    'button[name="add"]',
    '.add-to-cart-button',
    '.sticky-add-to-cart__button',
    'form[data-type="add-to-cart-form"] button[type="submit"]',
    'form[action*="/cart/add"] button[type="submit"]',
  ].join(', ');

  /** Express checkout wrappers we must never touch. */
  var ACCELERATED_SELECTOR =
    '.shopify-payment-button, .accelerated-checkout-block, shopify-accelerated-checkout, .payment-terms';

  function isAddToCartForm(form) {
    if (!(form instanceof HTMLFormElement)) return false;
    // The Shop Pay installments banner is a product form that is never submitted.
    if (form.classList.contains('payment-terms')) return false;
    if (form.getAttribute('data-type') === 'add-to-cart-form') return true;
    return (form.getAttribute('action') || '').indexOf('/cart/add') !== -1;
  }

  /**
   * Shop Pay / PayPal / Google Pay express buttons share the product form but are driven
   * by Shopify's own checkout scripts, and already bypass the cart. Never intercept them.
   * @param {Element | null} submitter
   */
  function isAcceleratedCheckout(submitter) {
    if (!submitter) return false;
    return Boolean(submitter.closest(ACCELERATED_SELECTOR));
  }

  /** Every add-to-cart button currently on the page, minus the express checkout ones. */
  function allButtons() {
    return Array.prototype.filter.call(
      document.querySelectorAll(BUTTON_SELECTOR),
      function (button) {
        return !isAcceleratedCheckout(button);
      }
    );
  }

  /**
   * Locks the UI while the checkout redirect is being prepared.
   * @param {HTMLElement | null} activeButton
   */
  function setBusy(activeButton) {
    document.documentElement.setAttribute('data-direct-checkout-busy', 'true');

    allButtons().forEach(function (button) {
      button.setAttribute('disabled', 'disabled');
    });

    if (activeButton) {
      activeButton.setAttribute('data-direct-checkout-loading', 'true');
      activeButton.setAttribute('aria-busy', 'true');
    }

    // The sticky bar stamps `data-added="true"` immediately after its synthetic click,
    // which the theme styles as `color: transparent` (the "Added" confirmation). That
    // state is meaningless here — nothing was added to a cart the buyer will ever see —
    // and it would blank out the label and the spinner. Strip it now and once more after
    // the current task, which is when the sticky handler sets it.
    stripAddedState();
    requestAnimationFrame(stripAddedState);
  }

  function stripAddedState() {
    allButtons().forEach(function (button) {
      button.removeAttribute('data-added');
    });
  }

  /** Restores the UI after a failure (never called on the success path — we navigate away). */
  function clearBusy() {
    document.documentElement.removeAttribute('data-direct-checkout-busy');

    allButtons().forEach(function (button) {
      // Buttons rendered as sold out / unavailable stay disabled.
      if (button.getAttribute('data-direct-checkout-sold-out') === 'true') return;
      button.removeAttribute('disabled');
      button.removeAttribute('data-direct-checkout-loading');
      button.removeAttribute('aria-busy');
    });
  }

  /**
   * Surfaces an error using the theme's existing inline error slot when available,
   * so messaging stays consistent with the rest of the product form.
   * @param {HTMLFormElement} form
   * @param {string} message
   */
  function showError(form, message) {
    var scope = form.closest('product-form-component') || form;
    var slot = scope.querySelector('[ref="addToCartTextError"]');

    if (slot) {
      slot.classList.remove('hidden');
      var textNode = slot.childNodes[2];
      if (textNode && textNode.nodeType === Node.TEXT_NODE) {
        textNode.textContent = message;
      } else {
        slot.appendChild(document.createTextNode(message));
      }
    }

    var live = scope.querySelector('[ref="liveRegion"]');
    if (live) live.textContent = message;

    if (!slot && !live) {
      // Last-resort fallback for surfaces without the theme's error markup.
      var fallback = form.querySelector('[data-direct-checkout-error]');
      if (!fallback) {
        fallback = document.createElement('small');
        fallback.setAttribute('data-direct-checkout-error', '');
        fallback.setAttribute('role', 'status');
        form.appendChild(fallback);
      }
      fallback.textContent = message;
    }
  }

  /** @param {HTMLFormElement} form */
  function hideError(form) {
    var scope = form.closest('product-form-component') || form;
    var slot = scope.querySelector('[ref="addToCartTextError"]');
    if (slot) slot.classList.add('hidden');
    var fallback = form.querySelector('[data-direct-checkout-error]');
    if (fallback) fallback.remove();
  }

  /**
   * @param {Response} response
   * @returns {Promise<any>}
   */
  function readJson(response) {
    return response.text().then(function (body) {
      try {
        return JSON.parse(body);
      } catch (error) {
        return null;
      }
    });
  }

  /* ------------------------------------------------------------------ *
   * Cart operations
   * ------------------------------------------------------------------ */

  function clearCart() {
    return fetch(CLEAR_URL, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: '{}',
    }).catch(function () {
      // A failed clear must not block the purchase; the add below still runs.
      return null;
    });
  }

  /**
   * @param {FormData} body
   * @returns {Promise<{ ok: boolean, message?: string }>}
   */
  function addToCart(body) {
    return fetch(ADD_URL, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
      body: body,
    }).then(function (response) {
      return readJson(response).then(function (data) {
        if (response.ok && data && !data.status) return { ok: true };

        var message =
          (data && (data.description || data.message)) ||
          (response.status === 422 ? STRINGS.unavailable : STRINGS.generic);

        return { ok: false, message: message };
      });
    });
  }

  /* ------------------------------------------------------------------ *
   * Main flow
   * ------------------------------------------------------------------ */

  /**
   * @param {HTMLFormElement} form
   * @param {HTMLElement | null} button
   */
  function payNow(form, button) {
    if (inFlight) return;

    // Honour native constraints (e.g. the gift-card recipient email field).
    if (typeof form.reportValidity === 'function' && !form.reportValidity()) return;

    var body = new FormData(form);

    // These are only meaningful to the non-AJAX endpoint / Section Rendering API.
    body.delete('return_to');
    body.delete('sections');
    body.delete('sections_url');

    var variantId = body.get('id');
    if (!variantId) {
      showError(form, STRINGS.unavailable);
      return;
    }

    if (!body.get('quantity')) body.set('quantity', '1');

    inFlight = true;
    hideError(form);
    setBusy(button);

    var start = REPLACE_CART ? clearCart() : Promise.resolve(null);

    start
      .then(function () {
        return addToCart(body);
      })
      .then(function (result) {
        if (!result.ok) {
          inFlight = false;
          clearBusy();
          showError(form, result.message || STRINGS.generic);
          return;
        }

        // Success: leave the button in its loading state through the navigation.
        window.location.assign(CHECKOUT_URL);
      })
      .catch(function () {
        inFlight = false;
        clearBusy();
        showError(form, STRINGS.generic);
      });
  }

  /* ------------------------------------------------------------------ *
   * Wiring
   * ------------------------------------------------------------------ */

  // Record the control the buyer actually pressed, so the pending state lands on the
  // button they are looking at rather than on a programmatic submitter.
  document.addEventListener(
    'pointerdown',
    function (event) {
      var target = event.target;
      if (!(target instanceof Element)) return;
      lastPressedButton = /** @type {HTMLElement | null} */ (target.closest(BUTTON_SELECTOR));
    },
    true
  );

  document.addEventListener(
    'submit',
    function (event) {
      var form = event.target;
      if (!isAddToCartForm(form)) return;

      var submitter = event.submitter instanceof HTMLElement ? event.submitter : null;
      if (isAcceleratedCheckout(submitter) || isAcceleratedCheckout(lastPressedButton)) return;

      // Take full ownership of the submission before Horizon's handler sees it.
      event.preventDefault();
      event.stopImmediatePropagation();

      var fallback = form.querySelector('button[name="add"], [type="submit"]');
      var button = lastPressedButton && lastPressedButton.isConnected ? lastPressedButton : submitter;

      payNow(form, button || fallback);
      lastPressedButton = null;
    },
    true
  );

  // Restore interactivity when the buyer returns via the back button (bfcache keeps
  // the disabled/loading state we left behind on the way to checkout).
  window.addEventListener('pageshow', function (event) {
    if (!event.persisted) return;
    inFlight = false;
    clearBusy();
  });
})();
